import { useEffect, useRef, useState, useCallback } from "react";
import { RefreshCw, X } from "lucide-react";
import api from "@/lib/api";
import { useLock } from "@/context/LockContext";

/**
 * VersionWatcher — spec iter87 (Feb 2026, rebuild v4).
 *
 * Polls `/api/version` frequently and, dès qu'une nouvelle version est
 * détectée, **recharge automatiquement le bundle client** après un court
 * décompte visible (banner 8 s). L'utilisateur peut annuler via ``×``, ou
 * forcer immédiatement via « Actualiser maintenant ».
 *
 * Sécurités :
 *   • Le rechargement est bloqué si un formulaire a du contenu non
 *     sauvegardé (input:focus contenant du texte, ou élément marqué
 *     ``data-dirty="true"``) — l'utilisateur voit alors le banner sans
 *     décompte et doit valider manuellement.
 *   • Polling en pause pendant que l'app est verrouillée (LockContext).
 *   • Au déverrouillage : re-baseline (une nouvelle version détectée
 *     PENDANT le lock est considérée comme ACK).
 *   • Session-storage dismiss persistant par version (v2 legacy).
 *
 * L'implémentation force le service worker à se désinscrire + ajoute un
 * cache-buster ``?_v=<ts>`` à l'URL pour garantir un vrai reload dur.
 */
const DISMISS_KEY = (version) => `desk:version-dismissed:${version}`;
const POLL_INTERVAL_MS = 30_000;      // 30 s (au lieu de 3 min avant)
const AUTO_RELOAD_DELAY_MS = 8_000;   // 8 s de countdown visible

const forceHardReload = async () => {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // non-fatal
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
};

/** Détecte si un formulaire a du contenu potentiellement non sauvegardé. */
function _hasUnsavedWork() {
  try {
    // 1. Un élément marqué explicitement comme dirty.
    if (document.querySelector('[data-dirty="true"]')) return true;
    // 2. Un input/textarea focus avec du contenu > 5 chars → probablement
    //    en cours de saisie active.
    const el = document.activeElement;
    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
      const v = (el.value || "").trim();
      if (v.length > 5) return true;
    }
    // 3. Un editor contentEditable actif.
    if (el && el.getAttribute && el.getAttribute("contenteditable") === "true") {
      const t = (el.innerText || "").trim();
      if (t.length > 5) return true;
    }
  } catch {}
  return false;
}

export default function VersionWatcher() {
  const { isLocked } = useLock();
  const initialRef = useRef(null);
  const [current, setCurrent] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(null);
  const [countdown, setCountdown] = useState(null); // null = pas de countdown
  const [autoReloadDisabled, setAutoReloadDisabled] = useState(false);
  const reloadTimerRef = useRef(null);

  const fetchVersion = useCallback(async () => {
    try {
      const { data } = await api.get("/version");
      if (initialRef.current === null) {
        initialRef.current = data.version;
      }
      setCurrent(data.version);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (isLocked) return undefined;
    let cancelled = false;
    const tick = async () => { if (!cancelled) await fetchVersion(); };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [isLocked, fetchVersion]);

  // Re-baseline au déverrouillage.
  const prevLockedRef = useRef(isLocked);
  useEffect(() => {
    if (prevLockedRef.current === true && isLocked === false) {
      initialRef.current = null;
      fetchVersion();
    }
    prevLockedRef.current = isLocked;
  }, [isLocked, fetchVersion]);

  useEffect(() => {
    if (!current) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY(current)) === "1") {
        setDismissedVersion(current);
      }
    } catch {}
  }, [current]);

  const initial = initialRef.current;
  const isStale =
    !isLocked && initial && current && initial !== current &&
    dismissedVersion !== current;

  // ---- AUTO-RELOAD countdown (spec iter87 – v4) --------------------------
  useEffect(() => {
    if (!isStale) {
      // reset if user dismissed / lock / same version
      if (reloadTimerRef.current) {
        clearInterval(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
      setCountdown(null);
      setAutoReloadDisabled(false);
      return undefined;
    }
    // Si formulaire dirty → banner sans countdown (attendre action manuelle).
    if (_hasUnsavedWork()) {
      setAutoReloadDisabled(true);
      setCountdown(null);
      return undefined;
    }
    setAutoReloadDisabled(false);
    // Démarre un countdown 8 → 0.
    let remaining = Math.ceil(AUTO_RELOAD_DELAY_MS / 1000);
    setCountdown(remaining);
    reloadTimerRef.current = setInterval(() => {
      // Re-check dirty state on every tick — un utilisateur peut
      // commencer à taper pendant le countdown.
      if (_hasUnsavedWork()) {
        clearInterval(reloadTimerRef.current);
        reloadTimerRef.current = null;
        setAutoReloadDisabled(true);
        setCountdown(null);
        return;
      }
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(reloadTimerRef.current);
        reloadTimerRef.current = null;
        forceHardReload();
        return;
      }
      setCountdown(remaining);
    }, 1000);
    return () => {
      if (reloadTimerRef.current) {
        clearInterval(reloadTimerRef.current);
        reloadTimerRef.current = null;
      }
    };
  }, [isStale]);

  const dismiss = () => {
    if (!current) return;
    try { sessionStorage.setItem(DISMISS_KEY(current), "1"); } catch {}
    setDismissedVersion(current);
    if (reloadTimerRef.current) {
      clearInterval(reloadTimerRef.current);
      reloadTimerRef.current = null;
    }
    setCountdown(null);
  };

  if (!isStale) return null;

  const label = autoReloadDisabled
    ? "Une nouvelle version est disponible — actualisez lorsque vous aurez fini de saisir."
    : (countdown !== null
      ? `Nouvelle version détectée — rechargement automatique dans ${countdown} s…`
      : "Une nouvelle version de Le Desk est disponible.");

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 rounded-full shadow-2xl border flex items-center gap-3"
      style={{
        background: "linear-gradient(135deg, #2B579A 0%, #1F4A80 100%)",
        borderColor: "rgba(255,255,255,0.15)",
        padding: "10px 14px 10px 16px",
        zIndex: 9998,
        color: "#FFFFFF",
        boxShadow: "0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)",
      }}
      data-testid="version-update-banner"
    >
      <span
        className="inline-flex items-center justify-center rounded-full flex-shrink-0"
        style={{ width: 22, height: 22, background: "rgba(255,255,255,0.20)" }}
      >
        <RefreshCw size={13} strokeWidth={2.4} className={countdown !== null ? "animate-spin" : ""} />
      </span>
      <span className="text-[12px] font-medium">{label}</span>
      <button
        type="button"
        onClick={forceHardReload}
        className="text-[11px] font-semibold rounded-full transition-colors"
        style={{
          padding: "5px 12px",
          background: "#FFFFFF",
          color: "#2B579A",
          border: "none",
          cursor: "pointer",
        }}
        data-testid="version-reload-btn"
      >
        Actualiser maintenant
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="inline-flex items-center justify-center rounded-full opacity-70 hover:opacity-100 transition-opacity"
        style={{ width: 22, height: 22, background: "transparent", border: "none", color: "#FFFFFF", cursor: "pointer" }}
        title="Fermer"
        data-testid="version-dismiss-btn"
      >
        <X size={13} />
      </button>
    </div>
  );
}
